/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";
import { Link } from "react-router-dom";
import podcastLogoImage from "podcast-logo-header.svg";
import homeLogoImage from "home-logo-no-text.svg";
import styled from "styled-components";
import { webComponentShim } from "components/shared/WebComponentShim";
import { useHistoryPush } from "utilities/NavigationUtilities";

export const enum LogoMode {
  STUDIO,
  DEFAULT,
}

const LogoButton = styled(webComponentShim("sp-action-button")).attrs({
  quiet: true,
})`
  width: 40px;
  height: 40px;
`;

export const Logo: React.FC<{ mode?: LogoMode }> = ({
  mode = LogoMode.DEFAULT,
}) => {
  const historyPush = useHistoryPush();
  const link = mode === LogoMode.STUDIO ? "/studio" : "/";
  const onClick = (): void => {
    historyPush(link);
  };

  switch (mode) {
    case LogoMode.STUDIO:
      return (
        <LogoButton data-testid="home-logo" onClick={onClick}>
          <img
            src={homeLogoImage}
            slot="icon"
            alt={I18n.t("landing.tools_lineup.studio.title")}
          />
        </LogoButton>
      );
    case LogoMode.DEFAULT:
      return (
        <LogoLink to={link}>
          <PodcastLogo alt={I18n.t("title")} data-testid="adobe-logo" />
        </LogoLink>
      );
  }
};

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: var(--spectrum-black);
`;

const PodcastLogo = styled.img.attrs({
  src: podcastLogoImage,
})`
  -webkit-user-drag: none;
`;
